import type { Feature, FeatureCollection, Polygon, Point } from 'geojson';
import { useMemo } from 'react';
import type { Project } from '../../projects/types';

/**
 * GeoJSON derivado de los bounds de cada proyecto.
 */
export interface ProjectGeoData {
  /** Polígonos de bounding box de cada proyecto mapeado. */
  boundsCollection: FeatureCollection<Polygon, { id: string; name: string }>;
  /** Centroides de cada proyecto mapeado. */
  centroidCollection: FeatureCollection<Point, { id: string; name: string }>;
  /** Cantidad de proyectos con bounds válidos. */
  mappableCount: number;
}

/**
 * Deriva GeoJSON territorial a partir del listado de proyectos.
 *
 * @remarks
 * Recibe `projects` ya cargados (sin fetch propio).
 * Usa `project.bounds` (BoundingBox, campo obligatorio) para:
 *   - Construir un polígono rectangular (bounds como Feature<Polygon>)
 *   - Calcular el centroide ((minLng+maxLng)/2, (minLat+maxLat)/2)
 *
 * Proyectos con span menor a 0.001° se descartan (bounds vacíos/inválidos).
 *
 * @param projects - Lista de proyectos desde adminProjectsApi.list()
 */
export function useProjectsGeoData(projects: Project[]): ProjectGeoData {
  return useMemo(() => {
    const boundsFeatures: Feature<Polygon, { id: string; name: string }>[] = [];
    const centroidFeatures: Feature<Point, { id: string; name: string }>[] = [];

    for (const project of projects) {
      const { minLng, minLat, maxLng, maxLat } = project.bounds;

      // Descartar bounds vacíos o degenerados
      if (maxLng - minLng < 0.001 || maxLat - minLat < 0.001) continue;

      const props = { id: project.id, name: project.name };

      boundsFeatures.push({
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [
            [
              [minLng, minLat],
              [maxLng, minLat],
              [maxLng, maxLat],
              [minLng, maxLat],
              [minLng, minLat], // cerrar anillo
            ],
          ],
        },
        properties: props,
      });

      centroidFeatures.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [(minLng + maxLng) / 2, (minLat + maxLat) / 2],
        },
        properties: props,
      });
    }

    return {
      boundsCollection: { type: 'FeatureCollection', features: boundsFeatures },
      centroidCollection: { type: 'FeatureCollection', features: centroidFeatures },
      mappableCount: boundsFeatures.length,
    };
  }, [projects]);
}
